import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TonConnectButton, useTonWallet } from "@tonconnect/ui-react";
import { useAppData } from "../context/AppDataContext";
import Avatar from "../components/Avatar";
import PullToRefresh from "../components/PullToRefresh";
import type { Challenge } from "../lib/api";

function ProfileTab() {
  const navigate = useNavigate();
  const wallet = useTonWallet();
  const [showExpired, setShowExpired] = useState(false);
  const { user, demoMode, allChallenges, balance, stats, isLoading, refresh } = useAppData();

  // Get current Telegram user info
  const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const username = telegramUser?.username || user?.telegramUsername || "User";
  const photoUrl = telegramUser?.photo_url || user?.telegramPhotoUrl;

  // Calculate additional stats
  // Note: EXPIRED challenges are not counted as "completed" since they weren't actually played
  const activeChallenges = allChallenges.filter(
    c => c.status === "OPEN" || c.status === "LOCKED" || c.status === "RESOLVING"
  ).length;
  const completedChallenges = allChallenges.filter(
    c => c.status === "SETTLED" || c.status === "REFUNDED"
  ).length;

  // Get recent completed challenges for history
  const hasAnyHistory = allChallenges.some(
    c => c.status === "SETTLED" || c.status === "REFUNDED" || c.status === "EXPIRED"
  );
  const recentHistory = allChallenges
    .filter(c => {
      if (c.status === "EXPIRED") return showExpired;
      return c.status === "SETTLED" || c.status === "REFUNDED";
    })
    .slice(0, 5);

  const getStatusColor = (status: Challenge["status"]): string => {
    switch (status) {
      case "SETTLED":
        return "blue";
      case "REFUNDED":
      case "EXPIRED":
        return "gray";
      default:
        return "gray";
    }
  };

  // Get current user telegram ID
  const currentUserId = telegramUser?.id?.toString();

  // Check if user is creator
  const isCreator = (challenge: Challenge) => {
    return challenge.creator?.telegramId === currentUserId;
  };

  // Get TON change for a challenge
  const getTonChange = (challenge: Challenge): { amount: number; label: string; className: string } | null => {
    if (challenge.status !== "SETTLED" && challenge.status !== "REFUNDED" && challenge.status !== "EXPIRED") return null;

    const positions = (challenge as any).positions || [];
    const userPosition = positions.find((p: any) => p.user?.telegramId === currentUserId);
    if (!userPosition) return null;

    const userStake = parseFloat(userPosition.amount);
    const userSide = userPosition.side;

    if (challenge.status === "REFUNDED" || challenge.status === "EXPIRED") {
      return { amount: 0, label: "±0", className: "neutral" };
    }

    if (challenge.outcome === userSide) {
      const poolTotal = parseFloat(challenge.poolTotal.toString());
      const profit = poolTotal - userStake;
      return { amount: profit, label: `+${profit.toFixed(1)}`, className: "win" };
    } else {
      return { amount: -userStake, label: `-${userStake.toFixed(1)}`, className: "loss" };
    }
  };

  const handleRefresh = async () => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred("medium");
    }
    await refresh();
  };

  const winRate = stats.wins + stats.losses > 0
    ? Math.round((stats.wins / (stats.wins + stats.losses)) * 100)
    : 0;

  return (
    <PullToRefresh onRefresh={refresh}>
      <div className="tab-content profile">
        {/* Profile Header */}
        <div className="profile-header">
        <Avatar username={username} photoUrl={photoUrl} size="lg" />
        <div className="profile-info">
          <h2 className="profile-name">@{username}</h2>
          {wallet && (
            <p className="profile-wallet">
              {wallet.account.address.slice(0, 6)}...{wallet.account.address.slice(-4)}
            </p>
          )}
        </div>
        <button className="refresh-btn" onClick={handleRefresh} disabled={isLoading}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </button>
      </div>

      {/* Balance Card */}
      <div className="balance-card profile-balance">
        <span className="label">{demoMode ? "Demo Balance" : "Balance"}</span>
        <span className="amount">{balance.toFixed(2)} TON</span>
        {!demoMode && stats.totalEarned !== 0 && (
          <span className={`earned-label ${stats.totalEarned < 0 ? "negative" : ""}`}>
            {stats.totalEarned >= 0 ? "+" : ""}{stats.totalEarned.toFixed(2)} TON net
          </span>
        )}
      </div>

      {/* Wallet Connect (Real Mode) */}
      {!demoMode && (
        <div className="wallet-connect-section">
          <TonConnectButton />
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-value wins">{stats.wins}</span>
          <span className="stat-label">Wins</span>
        </div>
        <div className="stat-card">
          <span className="stat-value losses">{stats.losses}</span>
          <span className="stat-label">Losses</span>
        </div>
        <div className="stat-card">
          <span className="stat-value refunds">{stats.draws}</span>
          <span className="stat-label">Refunds</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{winRate}%</span>
          <span className="stat-label">Win Rate</span>
        </div>
      </div>

      {/* Activity Summary */}
      <div className="activity-summary">
        <div className="activity-item">
          <span className="activity-count active">{activeChallenges}</span>
          <span className="activity-label">In Progress</span>
        </div>
        <div className="activity-item">
          <span className="activity-count">{completedChallenges}</span>
          <span className="activity-label">Finished</span>
        </div>
      </div>

      {/* Recent History */}
      {hasAnyHistory && (
        <div className="history-section">
          <div className="history-header">
            <h3>Recent History</h3>
            <label className="toggle-label small">
              <input
                type="checkbox"
                checked={showExpired}
                onChange={(e) => setShowExpired(e.target.checked)}
              />
              Show expired
            </label>
          </div>
          {recentHistory.length === 0 ? (
            <p className="history-empty">No settled challenges yet</p>
          ) : (
            <div className="history-list">
              {recentHistory.map((challenge) => {
                const tonChange = getTonChange(challenge);
                return (
                  <div
                    key={challenge.id}
                    className="history-item"
                    onClick={() => navigate(`/challenge/${challenge.id}`)}
                  >
                    <div className="history-content">
                      <p className="history-description">"{challenge.description}"</p>
                      <div className="history-meta">
                        <span className={`status ${getStatusColor(challenge.status)}`}>
                          {challenge.status === "REFUNDED" ? "Refunded" : challenge.status === "EXPIRED" ? "Expired" : "Settled"}
                        </span>
                        <span className="creator-tag">
                          {isCreator(challenge) ? "You" : `@${challenge.creator?.telegramUsername || "user"}`}
                        </span>
                      </div>
                    </div>
                    <div className="history-result">
                      {tonChange && (
                        <span className={`ton-change ${tonChange.className}`}>
                          {tonChange.label} TON
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Demo Mode Badge */}
      {demoMode && (
        <div className="demo-badge">
          <span className="demo-icon">&#x1F3AE;</span>
          <span>Demo Mode - No real TON involved</span>
        </div>
      )}

      {/* About Section */}
      <div className="about-section">
        <h3>About Pollygram</h3>
        <p className="about-tagline">Settle arguments with friends using TON</p>
        <div className="about-features">
          <div className="about-feature">
            <span className="feature-icon">&#x1F512;</span>
            <div>
              <strong>Secured by smart contracts</strong>
              <p>We never hold your funds. Your TON goes directly to a transparent smart contract.</p>
            </div>
          </div>
          <div className="about-feature">
            <span className="feature-icon">&#x2696;&#xFE0F;</span>
            <div>
              <strong>Fair resolution</strong>
              <p>Both sides vote on the outcome. Agree = winner gets paid. Disagree = everyone gets refunded.</p>
            </div>
          </div>
          <div className="about-feature">
            <span className="feature-icon">&#x26A1;</span>
            <div>
              <strong>Instant payouts</strong>
              <p>No waiting. When the challenge is resolved, funds are distributed automatically.</p>
            </div>
          </div>
        </div>
        <p className="about-version">v1.0 • Built on TON</p>
      </div>
      </div>
    </PullToRefresh>
  );
}

export default ProfileTab;
